import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Typography,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Alert,
  Snackbar,
} from '@mui/material';
import { ArrowBack, Download, CheckCircle, Replay } from '@mui/icons-material';
import PageHeader from '../../../components/PageHeader';
import LoadingLogo from '../../../components/LoadingLogo';
import EstadoChip from '../components/EstadoChip';
import PrioridadChip from '../components/PrioridadChip';
import CommentSection from '../components/CommentSection';
import sigcodService from '../services/sigcodService';

const SolicitudDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [request, setRequest] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const loadData = async () => {
    try {
      setLoading(true);
      const [reqRes, docsRes, commentsRes] = await Promise.all([
        sigcodService.getRequestById(id),
        sigcodService.getDocuments(id),
        sigcodService.getComments(id),
      ]);
      setRequest(reqRes.data);
      setDocuments(docsRes.data || []);
      setComments(commentsRes.data || []);
    } catch {
      setSnackbar({ open: true, message: 'Error al cargar solicitud', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, [id]);

  const handleDownload = async (docId) => {
    try {
      const res = await sigcodService.getDownloadUrl(id, docId);
      window.open(res.data.url, '_blank');
    } catch {
      setSnackbar({ open: true, message: 'Error al descargar', severity: 'error' });
    }
  };

  const handleConfirmReceipt = async () => {
    try {
      await sigcodService.confirmReceipt(id);
      setSnackbar({ open: true, message: 'Recepcion confirmada', severity: 'success' });
      loadData();
    } catch (error) {
      setSnackbar({ open: true, message: error.response?.data?.message || 'Error', severity: 'error' });
    }
  };

  const handleResubmit = async () => {
    try {
      await sigcodService.resubmitRequest(id, {});
      setSnackbar({ open: true, message: 'Solicitud reenviada', severity: 'success' });
      loadData();
    } catch (error) {
      setSnackbar({ open: true, message: error.response?.data?.message || 'Error', severity: 'error' });
    }
  };

  const handleDigitalReturn = async () => {
    try {
      await sigcodService.digitalReturn(id);
      setSnackbar({ open: true, message: 'Devolucion digital realizada', severity: 'success' });
      loadData();
    } catch (error) {
      setSnackbar({ open: true, message: error.response?.data?.message || 'Error', severity: 'error' });
    }
  };

  const handleAddComment = async (data) => {
    try {
      await sigcodService.addComment(id, data);
      const res = await sigcodService.getComments(id);
      setComments(res.data || []);
    } catch {
      setSnackbar({ open: true, message: 'Error al agregar comentario', severity: 'error' });
    }
  };

  const formatDate = (date) =>
    date ? new Date(date).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-';

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <LoadingLogo size={120} />
      </Box>
    );
  }

  if (!request) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Typography color="text.secondary">Solicitud no encontrada</Typography>
        <Button onClick={() => navigate('/sigcod/solicitudes')} sx={{ mt: 2 }}>Volver</Button>
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title={`Solicitud ${request.consecutivo}`}
        subtitle={`Tipo: ${request.tipo} | Creada: ${formatDate(request.createdAt)}`}
        actions={
          <Box sx={{ display: 'flex', gap: 1 }}>
            {request.estado === 'OBSERVADA' && (
              <Button variant="outlined" startIcon={<Replay />} onClick={handleResubmit}>
                Reenviar
              </Button>
            )}
            {['ENTREGADA_AL_CLIENTE', 'COMPARTIDA'].includes(request.estado) && (
              <Button variant="contained" color="success" startIcon={<CheckCircle />} onClick={handleConfirmReceipt}>
                Confirmar Recepcion
              </Button>
            )}
            {request.tipo === 'DIGITAL' && request.estado === 'COMPARTIDA' && (
              <Button variant="outlined" onClick={handleDigitalReturn}>
                Devolucion Digital
              </Button>
            )}
            <Button variant="outlined" startIcon={<ArrowBack />} onClick={() => navigate('/sigcod/solicitudes')}>
              Volver
            </Button>
          </Box>
        }
      />

      <Grid container spacing={2.5}>
        {/* Info */}
        <Grid item xs={12} md={8}>
          <Card elevation={0} sx={{ borderRadius: 0, border: '1px solid rgba(29,31,32,0.16)', mb: 2.5 }}>
            <CardContent>
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2 }}>
                Informacion General
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6} md={3}>
                  <Typography variant="caption" color="text.secondary">Estado</Typography>
                  <Box sx={{ mt: 0.5 }}><EstadoChip estado={request.estado} /></Box>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="caption" color="text.secondary">Prioridad</Typography>
                  <Box sx={{ mt: 0.5 }}><PrioridadChip prioridad={request.prioridad} /></Box>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="caption" color="text.secondary">Solicitante</Typography>
                  <Typography variant="body2" fontWeight={500}>{request.solicitante?.nombre}</Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="caption" color="text.secondary">Gestor</Typography>
                  <Typography variant="body2" fontWeight={500}>{request.gestor?.nombre || '-'}</Typography>
                </Grid>
                {request.bodegaOrigen && (
                  <Grid item xs={6} md={3}>
                    <Typography variant="caption" color="text.secondary">Bodega Origen</Typography>
                    <Typography variant="body2">{request.bodegaOrigen}</Typography>
                  </Grid>
                )}
                {request.fechaEntrega && (
                  <Grid item xs={6} md={3}>
                    <Typography variant="caption" color="text.secondary">Fecha Entrega</Typography>
                    <Typography variant="body2">{formatDate(request.fechaEntrega)}</Typography>
                  </Grid>
                )}
                {request.observaciones && (
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">Observaciones</Typography>
                    <Typography variant="body2">{request.observaciones}</Typography>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>

          {/* Documents */}
          <Card elevation={0} sx={{ borderRadius: 0, border: '1px solid rgba(29,31,32,0.16)' }}>
            <CardContent>
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2 }}>
                Documentos ({documents.length})
              </Typography>
              {documents.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                  Sin documentos asociados
                </Typography>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Nombre</TableCell>
                        <TableCell>Tipo</TableCell>
                        <TableCell>Caja</TableCell>
                        <TableCell>Carpeta</TableCell>
                        <TableCell>Estado</TableCell>
                        <TableCell align="center">Descargar</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {documents.map((doc) => (
                        <TableRow key={doc._id}>
                          <TableCell><Typography variant="body2">{doc.nombre}</Typography></TableCell>
                          <TableCell><Typography variant="caption">{doc.tipoDocumento || '-'}</Typography></TableCell>
                          <TableCell><Typography variant="caption">{doc.caja || '-'}</Typography></TableCell>
                          <TableCell><Typography variant="caption">{doc.carpeta || '-'}</Typography></TableCell>
                          <TableCell><EstadoChip estado={doc.estado} /></TableCell>
                          <TableCell align="center">
                            {doc.fileKey ? (
                              <Tooltip title="Descargar">
                                <IconButton size="small" onClick={() => handleDownload(doc._id)}>
                                  <Download fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            ) : '-'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Comments sidebar */}
        <Grid item xs={12} md={4}>
          <Card elevation={0} sx={{ borderRadius: 0, border: '1px solid rgba(29,31,32,0.16)' }}>
            <CardContent>
              <CommentSection
                comments={comments}
                onAddComment={handleAddComment}
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity} sx={{ width: '100%' }}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default SolicitudDetailPage;
